import React from 'react';
import { ControlledInput, ControlledSelect, ControlledSwitch } from '@@viewCommonComponents/controlled/index.js';
import { isRequiredSelect, isValidEmployerPercentShare } from '@@viewHelpers/validations/index.js';
import { Icon, InfoTooltip } from '@fasstech/middle-design-system';
import { mapIndexed } from '@@serverHelpers/index.mjs';
import classNames from 'classnames';
import { compose, equals, filter, find, findIndex, isNil, map, propEq } from 'ramda';
import ComplianceTag from './pricingHeader/ComplianceTag.jsx';

const MINIMUM_CCN_LABEL = 'Minimum CCN';
const pricingIsMinimumCcn = equals(MINIMUM_CCN_LABEL);

const PricingHeader = ({
  pricings,
  displayedPricings,
  getMiddleText,
  form,
  getMiddleBoolean,
  isSelected,
  minimumCCNIndex,
  tariffStructures,
  selectedTariffStructure,
  setSelectedOptions,
  effectDate,
  companyCcnHasAgreement
}) => {
  return (
    <div
      className={'grid'}
      style={{
        gridTemplateColumns: `300px repeat(${displayedPricings.length}, minmax(150px, 1fr))`
      }}
    >
      <div
        className={
          'flex flex-col w-full gap-4 mt-8 sticky left-0 bg-white z-10'
        }
      >
        <div className={'flex w-full p-4'}>
          <div className={'flex flex-col justify-start gap-4'}>
            <ControlledSelect
              name="tariffStructure"
              label={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_STRUCTURE_TARIFAIRE_LABEL'
              )}
              options={tariffStructures}
              form={form}
              extraChange={() => setSelectedOptions([])}
              isDisplayed={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_STRUCTURE_TARIFAIRE_EST_NECESSAIRE'
              )}
              rules={isRequiredSelect(
                tariffStructures,
                getMiddleBoolean(
                  'NIVEAUXGARANTIESETTARIFS_STRUCTURE_TARIFAIRE_EST_OBLIGATOIRE'
                )
              )}
              withIndicator={false}
              data-testid={'NIVEAUXGARANTIESETTARIFS_STRUCTURE_TARIFAIRE'}
            />
            <ControlledInput
              name="employerPercentShare"
              label={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_LABEL'
              )}
              form={form}
              number={true}
              mask="999 %"
              maskPlaceholder=" "
              displayErrorMessage
              rules={isValidEmployerPercentShare(
                getMiddleBoolean(
                  'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_EST_OBLIGATOIRE'
                )
              )}
              withIndicator={false}
              isDisplayed={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_EST_NECESSAIRE'
              )}
              tooltip={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_INFO_BULLE',
                {
                  employerPercentShareMin: '50',
                  employerPercentShareMax: '100'
                }
              )}
              data-testid={'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR'}
            />
            <ControlledSwitch
              name="beneficiariesEnrollment"
              label={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_AYANT_DROIT_LABEL'
              )}
              form={form}
              withIndicator={false}
              isDisplayed={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_AYANT_DROIT_EST_NECESSAIRE'
              )}
              tooltip={getMiddleText(
                'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_AYANT_DROIT_INFO_BULLE'
              )}
              data-testid={'NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_AYANT_DROIT'}
            />
          </div>
        </div>
      </div>
      {mapIndexed(
        ({ pricingLabel, pricingNote, isRecommended, idcc, prices }) => {
          const price = !pricingIsMinimumCcn(pricingLabel)
            ? find(
              propEq('tariffStructureCode', selectedTariffStructure),
              prices
            ) || {}
            : {};
          const pricingIndex = findIndex(
            propEq('pricingLabel', pricingLabel),
            pricings
          );
          const isPricingSelected = isSelected(pricingLabel);

          return (
            <div
              key={pricingLabel}
              className={
                'relative flex flex-col items-center mt-35 mx-1 border-t border-l border-r rounded-t-xl'
              }
            >
              {/*recommended*/}
              {isRecommended && (
                <div
                  className={
                    'absolute -top-28 flex flex-col items-center w-full'
                  }
                >
                  <Icon name="fiveStars" />
                  <span className={'text-primary text-[12px] font-bold'}>
                    RECOMMANDÉ
                  </span>
                </div>
              )}

              <ComplianceTag
                pricingIsMinimumCcn={pricingIsMinimumCcn(pricingLabel)}
                pricingIndex={pricingIndex}
                minimumCCNIndex={minimumCCNIndex}
                isDisplayed={companyCcnHasAgreement}
              />

              {/*minimum CCN tag TODO DS*/}
              {minimumCCNIndex !== -1 && pricingIndex === minimumCCNIndex && companyCcnHasAgreement && (
                <div className={'absolute -top-4 left-1'}>
                  <span
                    className={
                      'px-2 py-1 text-xs text-primary bg-white rounded-4xl shadow'
                    }
                  >
                    {MINIMUM_CCN_LABEL}
                  </span>
                </div>
              )}
              <div
                className={classNames(
                  'text-center border-t border-l border-r rounded-t-xl w-full',
                  {
                    'border-primary bg-primary text-white': isPricingSelected,
                    'bg-primary text-white':
                      !isSelected(pricingLabel) &&
                      !pricingIsMinimumCcn(pricingLabel),
                    'bg-[#F9F9FF] text-primary':
                      pricingIsMinimumCcn(pricingLabel)
                  }
                )}
              >
                <div
                  className={
                    'font-semibold p-2 text-xl flex gap-2 justify-center'
                  }
                >
                  {pricingLabel}
                  {pricingNote && (
                    <InfoTooltip
                      text={pricingNote}
                      className={
                        pricingIsMinimumCcn(pricingLabel)
                          ? ''
                          : '[&_path]:fill-white'
                      }
                    />
                  )}
                </div>
                <div
                  className={classNames(
                    'w-full h-auto text-sm  p-2',
                    isPricingSelected
                      ? 'bg-primary-300 text-white'
                      : 'bg-white text-black'
                  )}
                >
                  {pricingIsMinimumCcn(pricingLabel) ? (
                    <div>{`IDCC ${isNil(idcc) ? '9999' : idcc}`}</div>
                  ) : (
                    <>
                      {compose(
                        map(({ label, priceValue, priceUnit }) => (
                          <div
                            key={label}
                            className={classNames('mb-2 w-full', {
                              'text-white': isPricingSelected
                            })}
                          >
                            <div>{label}</div>
                            <div>
                              <span className={'font-bold text-xl'}>
                                {priceValue} {priceUnit}
                              </span>
                              {' / mois'}
                            </div>
                          </div>
                        )),
                        filter(
                          propEq('priceYear', String(effectDate.getFullYear()))
                        )
                      )(price.details || [])}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        }
      )(displayedPricings)}
    </div>
  );
};

export default PricingHeader;
